import { createSlice } from "@reduxjs/toolkit"
import {
  addAdminUser,
  deleteAdminUser,
  getAdminUserDetails,
  getAllBranchs,
  getAllRoles,
  getAllUsers,
  getAuthDetails,
  getUsersList,
  updateAdminUser,
  updateAdminUserStatus,
  restoreAdminUser,
} from "store/actions/usersAction"

const initialState = {
  loading: true,
  error: null,
  isLoading: false,
  actionType: null,
  message: null,
  totalCount: null,
  totalPages: null,
  currentPage: 1,
  itemsPerPage: 10,
  isUpdating: false,
  usersList: [],
  usersListStatus: null,
  userDetails: {},
  rolesList: [],
  branchsList: [],
  authDetails: [],
}

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // FETCH ALL USERS
    builder
      .addCase(getAllUsers.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.usersListStatus = null
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          const { totalCount, totalPages, currentPage } = action.payload
          // console.log(data)
          state.usersList = data
          state.totalCount = totalCount
          state.totalPages = totalPages
          state.currentPage = currentPage
          state.usersListStatus = Number(action.meta.arg?.status)
        }
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false
        state.usersListStatus = null
      })

    //  FETCH ALL ROLES LIST
    builder
      .addCase(getAllRoles.pending, state => {
        state.error = null
        state.actionType = null
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        const { status, data } = action.payload
        if (status) {
          state.rolesList = data
        }
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.loading = false
      })

    //  FETCH ALL USERS LIST
    builder
      .addCase(getUsersList.pending, state => {})
      .addCase(getUsersList.fulfilled, (state, action) => {
        const { status, data } = action.payload
        if (status) {
          state.usersList = data
        }
      })
      .addCase(getUsersList.rejected, (state, action) => {
        state.loading = false
      })

    //  FETCH ALL BRANCHS
    builder
      .addCase(getAllBranchs.pending, state => {
        state.error = null
        state.actionType = null
        state.branchsList = []
      })
      .addCase(getAllBranchs.fulfilled, (state, action) => {
        const { status, data } = action.payload
        if (status) {
          state.branchsList = data
        }
      })
      .addCase(getAllBranchs.rejected, (state, action) => {
        state.loading = false
      })

    // UPDATE USER STATUS
    builder
      .addCase(updateAdminUserStatus.pending, state => {
        state.error = null
        state.actionType = null
        state.isUpdating = true
      })
      .addCase(updateAdminUserStatus.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, status } = action.meta.arg
          state.usersList = state.usersList.map(user =>
            user._id === id ? { ...user, status: Number(status) } : user
          )
          state.message = action.payload.msg
          state.actionType = "USER_UPDATED_SUCCESS"
        } else {
          state.actionType = "USER_UPDATED_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(updateAdminUserStatus.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(restoreAdminUser.pending, state => {
        state.error = null
        state.actionType = null
        state.isUpdating = true
      })
      .addCase(restoreAdminUser.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id } = action.meta.arg
          state.usersList = state.usersList.filter(user => user._id !== id)
          state.message = action.payload.msg
          state.actionType = "USER_RESTORED_SUCCESS"
        } else {
          state.actionType = "USER_RESTORED_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(restoreAdminUser.rejected, state => {
        state.isUpdating = false
      })

    // ADD NEW ADMIN USER
    builder
      .addCase(addAdminUser.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
        state.message = null
      })
      .addCase(addAdminUser.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "ADDED_ADMIN_USER_SUCCESS"
          state.message = action.payload.msg
          action.meta.arg.navigate("/users")
        } else {
          state.actionType = "ADDED_ADMIN_USER_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(addAdminUser.rejected, (state, action) => {
        state.loading = false
      })

    // EDIT ADMIN USER
    builder
      .addCase(updateAdminUser.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
        state.message = null
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "UPDATED_ADMIN_USER_SUCCESS"
          state.message = action.payload.msg
        } else {
          state.actionType = "UPDATED_ADMIN_USER_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.isLoading = false
      })

    // DELETE ADMIN USER
    builder
      .addCase(deleteAdminUser.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        const { status, msg } = action.payload
        const { id, setShowDeleteModal } = action.meta.arg
        state.isLoading = false
        if (status) {
          state.usersList = state.usersList.filter(item => item._id !== id)
          state.error = null
          state.actionType = "ADMIN_USER_DELETE_SUCCESS"
          state.message = msg
          setShowDeleteModal(false)
          // Decrement the totalCount if it exists
          if (state.totalCount !== null) {
            state.totalCount -= 1
          }
        } else {
          state.actionType = "ADMIN_USER_DELETE_FAILED"
          state.error = msg
        }
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.isLoading = false
      })

    // GET USER DETAILS
    builder
      .addCase(getAdminUserDetails.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.userDetails = {}
      })
      .addCase(getAdminUserDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data, msg } = action.payload
        if (status) {
          state.userDetails = data
        } else {
          state.actionType = msg
        }
      })
      .addCase(getAdminUserDetails.rejected, (state, action) => {
        state.loading = false
      })

    //  FETCH USER DETAILS FOR PROFILE
    builder
      .addCase(getAuthDetails.pending, state => {
        state.loading = true
      })
      .addCase(getAuthDetails.fulfilled, (state, action) => {
        const { status, data } = action.payload
        if (status) {
          state.authDetails = data
        }
        state.loading = false
      })
      .addCase(getAuthDetails.rejected, (state, action) => {
        state.loading = false
      })
  },
})

export default usersSlice.reducer
